import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import type { PostCard_PostData } from '~publish/components/PostCard'

type PaginationSentinelIds = {
  /** Id of the post whose sentinel triggers `usePaginatedPostList`'s next page. */
  lastPostId: string | undefined
  /** Id of the content item whose sentinel triggers `usePaginatedContentItems`'s next page. */
  lastContentItemId: string | undefined
}

/**
 * The slice of a merged-queue entry this reads: the discriminant, plus the id
 * for content items. A full `ScheduledEntry` satisfies it.
 */
type SentinelEntry =
  | { kind: 'post' }
  | { kind: 'contentItem'; item: Pick<ContentItemDraft, 'id'> }

/**
 * Coordinates infinite scroll across the two independently-paginated draft
 * streams (posts and channel-less content items): each sentinel rides the
 * item furthest along in that stream's own loaded data, so scrolling past it
 * fetches more of whichever stream is behind in date coverage. Posts and
 * dated content items both sort ascending by their date field (`dueAt` /
 * `targetDate`), so the last dated content item by that sort is that stream's
 * frontier — not simply the last item in array order, which could differ if
 * the two streams load at different densities per page.
 */
export function getPaginationSentinelIds({
  posts,
  scheduledEntries,
  datedContentItems,
  undatedContentItems,
}: {
  posts: readonly Pick<PostCard_PostData, 'id'>[]
  scheduledEntries: readonly SentinelEntry[]
  datedContentItems: readonly Pick<ContentItemDraft, 'id'>[]
  undatedContentItems: readonly Pick<ContentItemDraft, 'id'>[]
}): PaginationSentinelIds {
  const lastPostId = posts.at(-1)?.id

  // Undated content items always render above the date queue, so their
  // sentinel only matters when there's nothing dated loaded yet.
  const lastContentItemId =
    datedContentItems.length > 0
      ? scheduledEntries.findLast(
          (e): e is Extract<SentinelEntry, { kind: 'contentItem' }> =>
            e.kind === 'contentItem',
        )?.item.id
      : undatedContentItems.at(-1)?.id

  return { lastPostId, lastContentItemId }
}
